import { SpendChartCard } from '@apps/web/components/admin/llm_spend/chart_shared';
import {
    generationSeriesFor,
    SCHEDULER_OBJECTIVE_SERIES,
} from '@apps/web/components/admin/llm_spend/constants';
import { GenerationSelect } from '@apps/web/components/admin/llm_spend/generation_select';
import { ObjectiveBreakdown } from '@apps/web/components/admin/llm_spend/objective_breakdown';
import { SpendLineChart } from '@apps/web/components/admin/llm_spend/spend_line_chart';
import {
    formatCount,
    formatUsd,
    generationRatioPoints,
    ratio,
} from '@apps/web/components/admin/llm_spend/spend_math';
import {
    SchedulerOrganizationsTable,
    SchedulerOutliersTable,
    SchedulerUsersTable,
} from '@apps/web/components/admin/llm_spend/spend_tables';
import { type GenerationTabProps } from '@apps/web/components/admin/llm_spend/tab_types';
import { KpiStatCard } from '@apps/web/components/admin/outreach_observability/kpi_stat_card';

export function SchedulerTab({
    data,
    bucket,
    generation,
    onGenerationChange,
}: GenerationTabProps) {
    const totals = data.scheduler_totals;
    const costPerConversationPoints = generationRatioPoints(
        data.scheduler_buckets,
        (metrics) => [
            metrics.tracked_estimated_spend_usd,
            metrics.conversation_count,
        ],
    );
    const callsPerConversationPoints = generationRatioPoints(
        data.scheduler_buckets,
        (metrics) => [metrics.llm_call_count, metrics.conversation_count],
    );
    const wakesPerConversationPoints = generationRatioPoints(
        data.scheduler_buckets,
        (metrics) => [metrics.wake_count, metrics.conversation_count],
    );
    const generationSeries = generationSeriesFor(generation);
    return (
        <div className="flex flex-col gap-4">
            <GenerationSelect
                value={generation}
                onValueChange={onGenerationChange}
            />
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <KpiStatCard
                    label="Scheduler spend"
                    value={formatUsd(totals.tracked_estimated_spend_usd)}
                    compact={true}
                />
                <KpiStatCard
                    label="Conversations"
                    value={totals.conversation_count.toLocaleString()}
                    compact={true}
                />
                <KpiStatCard
                    label="Spend per conversation"
                    value={formatUsd(
                        ratio(
                            totals.tracked_estimated_spend_usd,
                            totals.conversation_count,
                        ),
                    )}
                    compact={true}
                />
                <KpiStatCard
                    label="Activity per conversation"
                    value={`${formatCount(
                        ratio(totals.llm_call_count, totals.conversation_count),
                    )} LLM calls · ${formatCount(ratio(totals.wake_count, totals.conversation_count))} wakes`}
                    valueSize="sm"
                    compact={true}
                />
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
                <SpendChartCard title="Spend per scheduler conversation">
                    <SpendLineChart
                        points={costPerConversationPoints}
                        series={generationSeries}
                        bucket={bucket}
                        valueFormatter={formatUsd}
                    />
                </SpendChartCard>
                <SpendChartCard title="LLM calls per conversation">
                    <SpendLineChart
                        points={callsPerConversationPoints}
                        series={generationSeries}
                        bucket={bucket}
                        valueFormatter={formatCount}
                    />
                </SpendChartCard>
                <SpendChartCard title="Wakes per conversation">
                    <SpendLineChart
                        points={wakesPerConversationPoints}
                        series={generationSeries}
                        bucket={bucket}
                        valueFormatter={formatCount}
                    />
                </SpendChartCard>
            </div>
            <ObjectiveBreakdown
                title="Scheduler chat cost by objective"
                bucket={bucket}
                buckets={data.scheduler_buckets.map((row) => ({
                    bucketStart: row.bucket_start,
                    spend: row.spend_by_objective,
                    wakes: row.wakes_by_objective,
                    units: row.conversation_count,
                }))}
                series={SCHEDULER_OBJECTIVE_SERIES}
                totalUnits={totals.conversation_count}
                unitLabel="conversation"
            />
            <SchedulerOrganizationsTable
                rows={data.scheduler_top_organizations}
            />
            <SchedulerUsersTable rows={data.scheduler_top_users} />
            <SchedulerOutliersTable rows={data.scheduler_outliers} />
        </div>
    );
}
